/**
 * Store Module - Checkout Page
 *
 * Checkout flow with order summary and payment form placeholder.
 */
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useCartStore, selectCartTotal, selectCartItemCount } from '../store/cart-store'

interface PaymentFormState {
  fullName: string
  cardNumber: string
  expiry: string
  cvc: string
}

export default function Checkout() {
  const items = useCartStore((state) => state.items)
  const total = useCartStore(selectCartTotal)
  const itemCount = useCartStore(selectCartItemCount)
  const [payment, setPayment] = useState<PaymentFormState>({
    fullName: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
  })

  const isCartEmpty = items.length === 0

  const handleFieldChange = (field: keyof PaymentFormState, value: string) => {
    setPayment((previous) => ({ ...previous, [field]: value }))
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <h1 className="text-3xl font-bold text-tf-text mb-6">Checkout</h1>
      {isCartEmpty ? (
        <div className="bg-tf-bg-card shadow rounded-lg p-6">
          <p className="text-tf-text-muted mb-4">Your cart is empty. Add items before checkout.</p>
          <Link
            to="/store/products"
            className="inline-flex items-center rounded-lg bg-tf-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-tf-primary-hover"
          >
            Browse Products
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <section className="lg:col-span-2 rounded-lg bg-tf-bg-card shadow">
            <div className="border-b border-tf-border px-6 py-4">
              <h2 className="text-lg font-semibold text-tf-text">Payment Details</h2>
              <p className="text-sm text-tf-text-muted mt-1">Secure checkout form (Stripe-ready structure)</p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="full-name" className="mb-1.5 block text-sm font-medium text-tf-text-sub">
                  Full Name
                </label>
                <input
                  id="full-name"
                  type="text"
                  value={payment.fullName}
                  onChange={(event) => handleFieldChange('fullName', event.target.value)}
                  placeholder="Jane Doe"
                  className="w-full rounded-lg border border-tf-border bg-tf-bg-card px-3 py-2.5 text-sm text-tf-text placeholder:text-tf-text-muted focus:outline-none focus:ring-2 focus:ring-tf-ring"
                />
              </div>

              <div>
                <label htmlFor="card-number" className="mb-1.5 block text-sm font-medium text-tf-text-sub">
                  Card Number
                </label>
                <input
                  id="card-number"
                  type="text"
                  inputMode="numeric"
                  value={payment.cardNumber}
                  onChange={(event) => handleFieldChange('cardNumber', event.target.value)}
                  placeholder="4242 4242 4242 4242"
                  className="w-full rounded-lg border border-tf-border bg-tf-bg-card px-3 py-2.5 text-sm text-tf-text placeholder:text-tf-text-muted focus:outline-none focus:ring-2 focus:ring-tf-ring"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="expiry" className="mb-1.5 block text-sm font-medium text-tf-text-sub">
                    Expiry
                  </label>
                  <input
                    id="expiry"
                    type="text"
                    inputMode="numeric"
                    value={payment.expiry}
                    onChange={(event) => handleFieldChange('expiry', event.target.value)}
                    placeholder="MM/YY"
                    className="w-full rounded-lg border border-tf-border bg-tf-bg-card px-3 py-2.5 text-sm text-tf-text placeholder:text-tf-text-muted focus:outline-none focus:ring-2 focus:ring-tf-ring"
                  />
                </div>
                <div>
                  <label htmlFor="cvc" className="mb-1.5 block text-sm font-medium text-tf-text-sub">
                    CVC
                  </label>
                  <input
                    id="cvc"
                    type="text"
                    inputMode="numeric"
                    value={payment.cvc}
                    onChange={(event) => handleFieldChange('cvc', event.target.value)}
                    placeholder="123"
                    className="w-full rounded-lg border border-tf-border bg-tf-bg-card px-3 py-2.5 text-sm text-tf-text placeholder:text-tf-text-muted focus:outline-none focus:ring-2 focus:ring-tf-ring"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="mt-2 w-full rounded-lg bg-tf-primary px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-tf-primary-hover focus:outline-none focus:ring-2 focus:ring-tf-ring focus:ring-offset-2"
              >
                Place Order
              </button>
            </form>
          </section>

          <aside className="rounded-lg bg-tf-bg-card shadow h-fit">
            <div className="border-b border-tf-border px-6 py-4">
              <h2 className="text-base font-semibold text-tf-text">Order Summary</h2>
              <p className="text-sm text-tf-text-muted">{itemCount} {itemCount === 1 ? 'item' : 'items'}</p>
            </div>
            <div className="px-6 py-4">
              <ul className="space-y-2 text-sm">
                {items.map((item) => (
                  <li key={item.productId} className="flex items-center justify-between gap-4">
                    <span className="truncate text-tf-text-sub">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="font-medium text-tf-text">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="my-4 border-t border-tf-border" />

              <div className="flex items-center justify-between">
                <span className="text-base font-semibold text-tf-text">Total</span>
                <span className="text-xl font-extrabold text-tf-text">${total.toFixed(2)}</span>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  )
}
